import type { AppProfileRow } from '@/lib/database.types';
import type {
  MockProfile,
  Body,
  Position,
  Tribe,
  Relationship,
  LookingFor,
  Status,
  CockSize,
} from '@/data/mockProfiles';
import type { Scene } from '@/components/editorial/EditorialImage';
import type { HeatBand } from '@/modules/heat/types';

/**
 * Maps a raw AppProfileRow to the in-app MockProfile shape.
 *
 * Half-filled profiles still render — nullable fields fall back to safe
 * defaults so a missing column never throws in the UI.
 */
export const adaptRow = (row: AppProfileRow): MockProfile => {
  const primary = row.photo_url ?? '';
  const extras = (row.additional_photos ?? []).filter(Boolean);
  const photos = [primary, ...extras].filter(Boolean);

  return {
    id: row.id,
    ownerId: row.owner_id ?? undefined,
    handle: row.handle,
    mood: row.mood ?? '',
    bio: row.bio ?? '',
    interests: row.interests ?? [],
    isVerified: row.is_verified ?? false,
    distance:
      typeof row.distance_km === 'number' ? `${row.distance_km} km` : '— km',
    // The DB column is still named `room` (rolled forward semantically to
    // location / suburb). Expose as `location` on the app type.
    location: row.room ?? 'Melbourne',
    scene: ((row.scene as Scene) ?? 'club_room') as Scene,
    sceneSeed: row.scene_seed ?? 1,
    photos: photos.length > 0 ? photos : [''],
    band: (row.band ?? 'cool') as HeatBand,
    presence: row.presence ?? 'idle',
    age: row.age ?? undefined,
    height: row.height ?? '—',
    weight: row.weight ?? '—',
    body: ((row.body as Body) ?? 'average') as Body,
    position: ((row.position as Position) ?? 'vers') as Position,
    tribe: row.tribe ? ((row.tribe as Tribe) as Tribe) : undefined,
    relationship: ((row.relationship as Relationship) ?? 'single') as Relationship,
    lookingFor: (row.looking_for ?? []) as LookingFor[],
    status: ((row.status as Status) ?? 'ask me') as Status,
    lastTested: row.last_tested ?? undefined,
    cockSize: row.cock_size ? ((row.cock_size as CockSize) as CockSize) : undefined,
    mapX: row.map_x ?? 50,
    mapY: row.map_y ?? 50,
  };
};

/**
 * Drop rows whose owner_id is in the active sanctions set. Rows with no
 * owner_id are kept — they're orphan demo profiles, not real users.
 */
export const filterBanned = (
  rows: AppProfileRow[],
  bannedIds: Set<string>,
): AppProfileRow[] =>
  rows.filter((r) => !r.owner_id || !bannedIds.has(r.owner_id));
